import type { RuleStep } from '@edgesub/converter';
import { CloudCog, Database, Eye, FileText, Link2, Pencil, Plus, Trash2 } from 'lucide-react';
import { motion } from 'motion/react';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api';
import { RuleEditor } from '../components/RuleEditor';
import { ConnectivityResults } from '../components/ConnectivityResults';
import { Button, EmptyState, Field, IconButton, Modal, Spinner, useToast } from '../components/ui';
import { errorMessage, formatDate } from '../lib';
import type { ConnectivityProbe, Source, SourceDetail, SourcePreview } from '../types';

interface SourceDraft { name: string; type: 'remote' | 'manual'; url: string; userAgent: string; headers: string; content: string; steps: RuleStep[] }
const emptyDraft: SourceDraft = { name: '', type: 'remote', url: '', userAgent: '', headers: '', content: '', steps: [] };

function parseHeaders(value: string): Record<string, string> | undefined {
  const entries = value.split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
    const separator = line.indexOf(':');
    if (separator < 1) throw new Error(`请求头格式无效：${line}`);
    return [line.slice(0, separator).trim(), line.slice(separator + 1).trim()] as const;
  });
  return entries.length ? Object.fromEntries(entries) : undefined;
}

export function SourcesPage() {
  const [sources, setSources] = useState<Source[] | null>(null);
  const [draft, setDraft] = useState<SourceDraft>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<SourcePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [draftProbe, setDraftProbe] = useState<ConnectivityProbe | null>(null);
  const [draftProbing, setDraftProbing] = useState(false);
  const [savedProbe, setSavedProbe] = useState<ConnectivityProbe | null>(null);
  const [savedProbing, setSavedProbing] = useState(false);
  const [savedProbeSource, setSavedProbeSource] = useState<Source | null>(null);
  const initialDraft = useRef('');
  const draftProbeRequest = useRef(0);
  const savedProbeRequest = useRef(0);
  const { notify } = useToast();

  const load = useCallback(async () => setSources(await api.get<Source[]>('/api/sources')), []);
  useEffect(() => { load().catch((error) => notify(errorMessage(error), 'error')); }, [load, notify]);

  const clearDraftProbe = () => {
    draftProbeRequest.current += 1;
    setDraftProbe(null);
    setDraftProbing(false);
  };
  const closeEditor = () => { clearDraftProbe(); setOpen(false); };
  const closeSavedProbe = () => {
    savedProbeRequest.current += 1;
    setSavedProbeSource(null);
    setSavedProbe(null);
    setSavedProbing(false);
  };

  // Any edit invalidates previous derived results, including an in-flight probe.
  const update = (patch: Partial<SourceDraft>) => { setDraft((current) => ({ ...current, ...patch })); setPreview(null); clearDraftProbe(); };

  const openWith = (next: SourceDraft, id: string | null) => {
    setDraft(next);
    initialDraft.current = JSON.stringify(next);
    setEditingId(id);
    setPreview(null);
    clearDraftProbe();
    setOpen(true);
  };
  const create = () => openWith(emptyDraft, null);
  const edit = async (source: Source) => {
    try {
      const detail = await api.get<SourceDetail>(`/api/sources/${source.id}`);
      const payload = detail.payload;
      openWith('url' in payload ? {
        name: detail.name, type: 'remote', url: payload.url, userAgent: payload.userAgent ?? '',
        headers: Object.entries(payload.headers ?? {}).map(([key, value]) => `${key}: ${value}`).join('\n'), content: '', steps: payload.steps ?? [],
      } : {
        name: detail.name, type: 'manual', url: '', userAgent: '', headers: '', content: payload.content, steps: payload.steps ?? [],
      }, source.id);
    } catch (error) { notify(errorMessage(error), 'error'); }
  };

  // Shared between save and preview so both send the exact same payload shape.
  const buildPayload = () => {
    const steps = draft.steps.length ? draft.steps : undefined;
    return draft.type === 'remote'
      ? { type: 'remote' as const, payload: { url: draft.url, userAgent: draft.userAgent || undefined, headers: parseHeaders(draft.headers), steps } }
      : { type: 'manual' as const, payload: { content: draft.content, steps } };
  };

  const previewDisabled = previewing || (draft.type === 'remote' ? !draft.url : !draft.content.trim());
  const runPreview = async () => {
    setPreviewing(true);
    try {
      setPreview(await api.post<SourcePreview>('/api/sources/preview', buildPayload()));
    } catch (error) { notify(errorMessage(error), 'error'); } finally { setPreviewing(false); }
  };

  const runDraftProbe = async () => {
    const requestId = ++draftProbeRequest.current;
    setDraftProbing(true);
    setDraftProbe(null);
    try {
      const result = await api.post<ConnectivityProbe>('/api/sources/probe', buildPayload());
      if (draftProbeRequest.current === requestId) setDraftProbe(result);
    } catch (error) {
      if (draftProbeRequest.current === requestId) notify(errorMessage(error), 'error');
    } finally {
      if (draftProbeRequest.current === requestId) setDraftProbing(false);
    }
  };

  const runSavedProbe = async (source: Source) => {
    const requestId = ++savedProbeRequest.current;
    setSavedProbeSource(source);
    setSavedProbing(true);
    setSavedProbe(null);
    try {
      const result = await api.post<ConnectivityProbe>(`/api/sources/${source.id}/probe`);
      if (savedProbeRequest.current === requestId) setSavedProbe(result);
    } catch (error) {
      if (savedProbeRequest.current === requestId) {
        setSavedProbeSource(null);
        notify(errorMessage(error), 'error');
      }
    } finally {
      if (savedProbeRequest.current === requestId) setSavedProbing(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const body = { name: draft.name, ...buildPayload() };
      if (editingId) await api.put(`/api/sources/${editingId}`, body); else await api.post('/api/sources', body);
      notify(editingId ? '订阅源已更新' : '订阅源已创建');
      closeEditor();
      await load();
    } catch (error) { notify(errorMessage(error), 'error'); } finally { setBusy(false); }
  };

  const remove = async (source: Source) => {
    if (!window.confirm(`删除订阅源“${source.name}”？`)) return;
    try { await api.delete(`/api/sources/${source.id}`); notify('订阅源已删除'); await load(); } catch (error) { notify(errorMessage(error), 'error'); }
  };

  return <>
    <header className="page-header"><div><span className="eyebrow">输入管理</span><h1>订阅源</h1><p>保存远程订阅地址或直接维护节点文本。</p></div><Button icon={<Plus size={17} />} onClick={create}>添加订阅源</Button></header>
    {!sources ? <Spinner /> : sources.length === 0 ? <EmptyState icon={<Database size={24} />} title="还没有订阅源" action={<Button icon={<Plus size={16} />} onClick={create}>添加第一个</Button>} /> : <section className="item-list">{sources.map((source, index) => <motion.article className="item-card" key={source.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.045, 0.22), duration: 0.24 }}>
      <div className={`item-type ${source.type === 'remote' ? 'blue' : 'teal'}`}>{source.type === 'remote' ? <Link2 size={19} /> : <FileText size={19} />}</div>
      <div className="item-main"><div className="item-title"><h2>{source.name}</h2><span className="tag">{source.type === 'remote' ? '远程 URL' : '手动节点'}</span></div><p>更新于 {formatDate(source.updatedAt)}</p></div>
      <div className="item-actions"><IconButton label="云端检测" disabled={savedProbing} onClick={() => void runSavedProbe(source)}><CloudCog size={17} /></IconButton><IconButton label="编辑" onClick={() => void edit(source)}><Pencil size={17} /></IconButton><IconButton label="删除" onClick={() => void remove(source)}><Trash2 size={17} /></IconButton></div>
    </motion.article>)}</section>}
    <Modal open={open} title={editingId ? '编辑订阅源' : '添加订阅源'} onClose={closeEditor} wide isDirty={() => JSON.stringify(draft) !== initialDraft.current}>
      <form className="form-stack" onSubmit={submit}>
        <Field label="名称"><input value={draft.name} onChange={(event) => update({ name: event.target.value })} maxLength={80} required /></Field>
        <div className="segmented"><button type="button" className={draft.type === 'remote' ? 'active' : ''} onClick={() => update({ type: 'remote' })}><Link2 size={16} />远程 URL</button><button type="button" className={draft.type === 'manual' ? 'active' : ''} onClick={() => update({ type: 'manual' })}><FileText size={16} />手动节点</button></div>
        {draft.type === 'remote' ? <>
          <Field label="订阅地址"><input type="url" value={draft.url} onChange={(event) => update({ url: event.target.value })} placeholder="https://example.com/subscription" maxLength={2048} required /></Field>
          <Field label="User-Agent" hint="留空时使用 EdgeSub 默认标识"><input value={draft.userAgent} onChange={(event) => update({ userAgent: event.target.value })} placeholder="clash.meta" maxLength={256} /></Field>
          <Field label="请求头" hint="每行一个，格式为 Header: Value"><textarea className="compact-textarea" value={draft.headers} onChange={(event) => update({ headers: event.target.value })} placeholder="Authorization: Bearer ..." /></Field>
        </> : <Field label="节点内容" hint="支持 URI、Clash/Mihomo、Surge、Loon 与 Quantumult X"><textarea className="code-textarea" value={draft.content} onChange={(event) => update({ content: event.target.value })} placeholder="粘贴节点或订阅内容" required /></Field>}

        <div className="source-processing">
          <div className="source-processing-head"><span className="field-label">节点处理<span className="field-optional">可选</span></span><div className="source-processing-actions"><Button type="button" variant="secondary" icon={<Eye size={15} />} onClick={() => void runPreview()} disabled={previewDisabled}>{previewing ? '预览中…' : '预览节点名称'}</Button><Button type="button" variant="secondary" icon={<CloudCog size={15} />} onClick={() => void runDraftProbe()} disabled={previewDisabled || draftProbing}>{draftProbing ? '检测中…' : '云端检测'}</Button></div></div>
          <p className="field-hint">在此重命名、筛选或排序节点；处理后的结果会被组合、发布与文件直接使用。</p>
          <RuleEditor steps={draft.steps} onChange={(steps) => update({ steps })} />
          {preview && <div className="name-preview">
            <div className="name-preview-summary">共 <strong>{preview.nodeCount}</strong> 个节点{preview.originalCount !== preview.nodeCount ? ` · 处理前 ${preview.originalCount}` : ''}</div>
            {preview.names.length === 0 ? <div className="inline-empty compact">没有匹配的节点。</div> : <ol className="name-preview-list">{preview.names.map((name, index) => <li key={index}><span className="name-index">{index + 1}</span><span className="name-text">{name || '（空名称）'}</span></li>)}</ol>}
            {preview.truncated && <p className="step-limit">仅显示前 {preview.names.length} 个名称。</p>}
          </div>}
          {draftProbe && <ConnectivityResults probe={draftProbe} />}
        </div>

        <div className="form-actions"><Button type="button" variant="secondary" onClick={closeEditor}>取消</Button><Button type="submit" disabled={busy}>{busy ? '保存中...' : '保存'}</Button></div>
      </form>
    </Modal>
    <Modal open={savedProbeSource !== null} title={savedProbeSource ? `${savedProbeSource.name} · 云端检测` : '云端检测'} onClose={closeSavedProbe} wide>
      {savedProbing && !savedProbe ? <div className="probe-loading"><Spinner /><p>正在从 Cloudflare 边缘检测节点端点…</p></div> : savedProbe ? <ConnectivityResults probe={savedProbe} /> : null}
    </Modal>
  </>;
}
